import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import { CLIENTS_COLLECTION } from '@/modules/commercial/services/constants';
import type { Client, ClientInput } from '@/modules/commercial/types';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type ClientFirestoreDoc = {
  companyId: string;
  type: Client['type'];
  name: string;
  businessName: string;
  taxId: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapClient(id: string, data: ClientFirestoreDoc): Client {
  return {
    id,
    companyId: data.companyId,
    type: data.type,
    name: data.name,
    businessName: data.businessName ?? '',
    taxId: data.taxId ?? '',
    email: data.email ?? '',
    phone: data.phone ?? '',
    address: data.address ?? '',
    notes: data.notes ?? '',
    active: data.active,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listClients(
  companyId: string = appConfig.companyId,
): Promise<Client[]> {
  const q = query(
    collection(getFirestoreDb(), CLIENTS_COLLECTION),
    where('companyId', '==', companyId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapClient(d.id, d.data() as ClientFirestoreDoc))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function getClient(id: string): Promise<Client | null> {
  const snap = await getDoc(doc(getFirestoreDb(), CLIENTS_COLLECTION, id));
  if (!snap.exists()) return null;
  return mapClient(snap.id, snap.data() as ClientFirestoreDoc);
}

export async function saveClient(
  input: ClientInput,
  id?: string,
): Promise<Client> {
  return withFirestoreError(async () => {
  const ref = id
    ? doc(getFirestoreDb(), CLIENTS_COLLECTION, id)
    : doc(collection(getFirestoreDb(), CLIENTS_COLLECTION));
  const existing = await getDoc(ref);
  const before = existing.exists()
    ? toAuditSnapshot(mapClient(existing.id, existing.data() as ClientFirestoreDoc))
    : undefined;
  const payload = {
    companyId: appConfig.companyId,
    type: input.type,
    name: input.name.trim(),
    businessName: input.businessName.trim(),
    taxId: input.taxId.trim(),
    email: input.email.trim(),
    phone: input.phone.trim(),
    address: input.address.trim(),
    notes: input.notes.trim(),
    active: input.active,
    ...buildTimestampPayload(!existing.exists()),
  };
  await setDoc(ref, payload, { merge: true });
  const saved = await getDoc(ref);
  if (!saved.exists()) throw new Error('No se pudo guardar el cliente.');
  const result = mapClient(saved.id, saved.data() as ClientFirestoreDoc);
  recordEntityWrite({
    entityType: 'client',
    entityId: result.id,
    isCreate: !existing.exists(),
    ...(before !== undefined ? { before } : {}),
    after: toAuditSnapshot(result, ['taxId']),
  });
  return result;
  }, 'client');
}
